import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getEmployeePeerReviewCyclesAPI, 
  getEmployeePeerReviewEmployeesAPI,
  getEmployeeAppraisalResponsesAPI
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import { toast } from "react-toastify";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface CycleWithEmployees extends AvailableCycle {
  completedEmployees: Employee[];
  pendingEmployees: Employee[];
}

const PeerReviewTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [peerReviewCyclesWithEmployees, setPeerReviewCyclesWithEmployees] = useState<CycleWithEmployees[]>([]);
  const [completedPeerResponses, setCompletedPeerResponses] = useState<AppraisalResponse[]>([]);

  useEffect(() => {
    fetchPeerReviewCycles();
    fetchCompletedResponses();
  }, []);

  const fetchPeerReviewCycles = async () => {
    try {
      setLoading(true);
      const response = await getEmployeePeerReviewCyclesAPI();
      const cycles = response.data;
      
      const cyclesWithStatus = await Promise.all(
        cycles.map(async (cycle) => {
          try {
            const statusResponse = await getEmployeePeerReviewEmployeesAPI(cycle.id);
            const completed = statusResponse.data.completed || [];
            const pending = statusResponse.data.pending || [];
            
            return {
              ...cycle,
              completedEmployees: completed,
              pendingEmployees: pending
            };
          } catch (err) {
            console.error(`Failed to fetch peer employees for cycle ${cycle.id}:`, err);
            return {
              ...cycle,
              completedEmployees: [],
              pendingEmployees: []
            };
          }
        })
      );
      
      setPeerReviewCyclesWithEmployees(cyclesWithStatus);
    } catch (err: any) {
      toast.error("Failed to load peer review cycles");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedResponses = async () => {
    try {
      const response = await getEmployeeAppraisalResponsesAPI('PEER', 'SUBMITTED');
      setCompletedPeerResponses(response.data);
    } catch (err: any) {
      console.error("Failed to load completed responses:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  if (loading && peerReviewCyclesWithEmployees.length === 0) {
    return (
      <Card extra="p-4">
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          Loading peer reviews...
        </div>
      </Card>
    );
  }

  return (
    <Card extra="p-4">
      <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
        Peer Reviews
      </h2>

      {peerReviewCyclesWithEmployees.length === 0 ? (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          No peer review cycles available at this time.
        </div>
      ) : (
        <div className="space-y-4">
          {peerReviewCyclesWithEmployees.map((cycle) => (
            <div
              key={cycle.id}
              className="border border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                    {cycle.name}
                  </h3>
                  {cycle.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {cycle.description}
                    </p>
                  )}
                </div>
                {isDeadlinePassed(cycle.deadline) && (
                  <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 dark:bg-red-900/20 rounded">
                    Deadline Passed
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Form Opens: </span>
                  <span className="text-navy-700 dark:text-white font-medium">
                    {formatDate(cycle.form_opens)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Deadline: </span>
                  <span className="text-navy-700 dark:text-white font-medium">
                    {formatDate(cycle.deadline)}
                  </span>
                </div>
              </div>

              {cycle.completedEmployees && cycle.completedEmployees.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                    Completed ({cycle.completedEmployees.length}):
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cycle.completedEmployees.map((employee) => {
                      const response = completedPeerResponses.find(
                        r => r.cycle.id === cycle.id && r.employee.id === employee.id
                      );
                      return (
                        <div
                          key={employee.id}
                          onClick={() => response && navigate(`/employee-appraisals/response/${response.id}`)}
                          className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer ${
                            isDeadlinePassed(cycle.deadline) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {employee.photo ? (
                              <img
                                src={employee.photo}
                                alt={employee.full_name || "Employee"}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                  {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-navy-700 dark:text-white truncate">
                                {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                              </p>
                              {employee.email && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {employee.email}
                                </p>
                              )}
                            </div>
                            <i className="fas fa-check-circle text-gray-500 dark:text-gray-400 text-lg"></i>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {cycle.pendingEmployees && cycle.pendingEmployees.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                    Pending ({cycle.pendingEmployees.length}):
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cycle.pendingEmployees.map((employee) => (
                      <div
                        key={employee.id}
                        onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/employee-appraisals/peer/${cycle.id}/employee/${employee.id}`)}
                        className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer ${
                          isDeadlinePassed(cycle.deadline) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {employee.photo ? (
                            <img
                              src={employee.photo}
                              alt={employee.full_name || "Employee"}
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                              <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-navy-700 dark:text-white truncate">
                              {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                            </p>
                            {employee.email && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {employee.email}
                              </p>
                            )}
                            {employee.designation && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                {employee.designation}
                              </p>
                            )}
                          </div>
                          <i className="fas fa-clock text-gray-500 dark:text-gray-400 text-lg"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default PeerReviewTab;
